// Frontend mirror of backend/src/config/beerStyles.json's beerStyleGroups / coreStyleToGroup
// and the classification thresholds in backend/src/utils/beerClassification.mjs.
// Kept in sync manually with the backend config (same style as beerStyles.json vs beerStyles.mjs).

export const BEER_STYLE_GROUPS = [
  {
    group_name: "Crisp & Easy Beer",
    body: "Light",
    texture: "Crisp, Refreshing",
    flavor_intensity: "Light - Medium, Clean",
    core_styles: [
      "American Light Lager",
      "American/International Lager",
      "Pilsner",
      "Helles / Munich Helles",
      "Blonde / Golden Ale",
    ],
  },
  {
    group_name: "Balanced & Toasty Beer",
    body: "Medium",
    texture: "Smooth, Rounded",
    flavor_intensity: "Medium, Toasty",
    core_styles: ["Vienna / Märzen (Amber Lager)", "Amber / Red Ale", "Brown Ale", "Dark Lager"],
  },
  {
    group_name: "Bright & Hoppy Beer",
    body: "Light - Medium",
    texture: "Crisp to Soft",
    flavor_intensity: "Medium - High, Bright & Hoppy",
    core_styles: ["Pale Ale (American)", "IPA", "Hazy IPA (NEIPA)"],
  },
  {
    group_name: "Fresh & Lively Beer",
    body: "Light - Medium",
    texture: "Lively, Effervescent",
    flavor_intensity: "Medium - High, Fruity or Spiced",
    core_styles: ["Wheat Beer", "Saison / Farmhouse Ale", "Belgian Strong"],
  },
  {
    group_name: "Rich & Dark Beer",
    body: "Medium - Full",
    texture: "Creamy, Substantial",
    flavor_intensity: "Medium - High, Roasted",
    core_styles: ["Porter", "Stout", "Imperial Stout"],
  },
];

export const CORE_STYLE_TO_GROUP = BEER_STYLE_GROUPS.reduce((acc, group) => {
  group.core_styles.forEach((style) => {
    acc[style] = group.group_name;
  });
  return acc;
}, {});

// The flat list of the 18 approved Core Styles - the single source of truth
// for every Beer Style picker (Add/Edit Beer, Bulk Change Style, etc.) so
// the lists cannot drift out of sync with each other or with classification.
export const CORE_STYLES = BEER_STYLE_GROUPS.flatMap((group) => group.core_styles);

export function getStyleProfile(styleName) {
  if (!styleName) return null;
  const groupName = CORE_STYLE_TO_GROUP[styleName];
  if (!groupName) return null;
  const group = BEER_STYLE_GROUPS.find((g) => g.group_name === groupName);
  if (!group) return null;
  return {
    groupName: group.group_name,
    body: group.body,
    texture: group.texture,
    flavorIntensity: group.flavor_intensity,
  };
}

// Non-overlapping, gap-free cascade (matches backend/src/utils/beerStrength.mjs).
// Non-Alcoholic requires STRICTLY less than 0.5% ABV (TTB threshold) - exactly
// 0.5% is alcoholic and belongs to Light.
export function getAbvCategory(abv) {
  if (abv === undefined || abv === null || abv === "") return null;
  const val = Number(abv);
  if (isNaN(val) || val < 0) return null;
  if (val < 0.5) return "Non-Alcoholic";
  if (val <= 4.5) return "Light";
  if (val <= 7.5) return "Medium";
  return "Strong";
}

export function getIbuCategory(ibu) {
  if (ibu === undefined || ibu === null || ibu === "") return null;
  const val = Number(ibu);
  if (isNaN(val)) return null;
  if (val <= 20) return "Low";
  if (val <= 45) return "Medium";
  return "High";
}

export function getGroupBadgeColor(groupName) {
  const cat = (groupName || "").toLowerCase();
  if (cat.includes("crisp") || cat.includes("clean")) return "bg-[#EFF6FF] text-[#2563EB] border-[#BFDBFE]";
  if (cat.includes("balanced") || cat.includes("toasty")) return "bg-[#FEFCE8] text-[#CA8A04] border-[#FEF08A]";
  if (cat.includes("bright") || cat.includes("hoppy")) return "bg-[#F0FDF4] text-[#16A34A] border-[#BBF7D0]";
  if (cat.includes("fresh") || cat.includes("lively") || cat.includes("fruity")) return "bg-[#FAF5FF] text-[#9333EA] border-[#E9D5FF]";
  if (cat.includes("rich") || cat.includes("dark")) return "bg-[#111827] text-white border-[#1F2937]";
  return "bg-gray-50 text-gray-700 border-gray-200";
}

// Solid hex matching each group's badge text color, for use as a progress-bar fill.
export function getGroupBarColor(groupName) {
  const cat = (groupName || "").toLowerCase();
  if (cat.includes("crisp") || cat.includes("clean")) return "#2563EB";
  if (cat.includes("balanced") || cat.includes("toasty")) return "#CA8A04";
  if (cat.includes("bright") || cat.includes("hoppy")) return "#16A34A";
  if (cat.includes("fresh") || cat.includes("lively") || cat.includes("fruity")) return "#9333EA";
  if (cat.includes("rich") || cat.includes("dark")) return "#111827";
  return "#9CA3AF";
}

export function getLevelBadgeColor(level) {
  const lvl = (level || "").toLowerCase();
  if (lvl.includes("non-alcoholic") || lvl === "low" || lvl === "light") return "text-[#2563EB] bg-[#EFF6FF] border-[#BFDBFE]";
  if (lvl === "medium") return "text-[#CA8A04] bg-[#FEFCE8] border-[#FEF08A]";
  if (lvl === "high" || lvl === "strong") return "text-[#DC2626] bg-[#FEF2F2] border-[#FECACA]";
  return "text-gray-700 bg-gray-50 border-gray-200";
}

// Mirrors backend/src/config/beerStyles.json's styleToFamily.
export const STYLE_TO_FAMILY = {
  "American Light Lager": "Lager",
  "American/International Lager": "Lager",
  "Pilsner": "Lager",
  "Helles / Munich Helles": "Lager",
  "Vienna / Märzen (Amber Lager)": "Lager",
  "Dark Lager": "Lager",
  "Blonde / Golden Ale": "Ale",
  "Pale Ale (American)": "Ale",
  "IPA": "Ale",
  "Hazy IPA (NEIPA)": "Ale",
  "Wheat Beer": "Ale",
  "Saison / Farmhouse Ale": "Ale",
  "Belgian Strong": "Ale",
  "Amber / Red Ale": "Ale",
  "Brown Ale": "Ale",
  "Porter": "Ale",
  "Stout": "Ale",
  "Imperial Stout": "Ale",
};

export function getFermentationFamily(styleName) {
  if (!styleName) return null;
  return STYLE_TO_FAMILY[styleName] || null;
}

const DOMESTIC_COUNTRY_ALIASES = new Set(["usa", "us", "united states", "united states of america"]);

export function getOriginClassification(originCountry) {
  const normalized = (originCountry || "").trim().toLowerCase();
  if (!normalized) return null;
  return DOMESTIC_COUNTRY_ALIASES.has(normalized) ? "DOMESTIC" : "IMPORTED";
}

export function isImportedFromCountry(originCountry) {
  const classification = getOriginClassification(originCountry);
  if (!classification) return null;
  return classification === "IMPORTED";
}
